import { Button, Dialog, RadixDialogPrimitives } from '@capsule/consumer-design-system';

import { useHistory, useParams } from 'react-router';
import styled, { css } from 'styled-components';

import { DoormanArt } from 'src/app/components/Pages/Order/FailedOrderReschedule/DoormanArt';
import { editDeliveryAddressRequestBodyTransform } from 'src/app/components/Pages/Order/FailedOrderReschedule/utils';
import { useDeliveryAddressUpdate } from 'src/app/hooks/data/deliveryAddress';
import { useGetOrder } from 'src/app/hooks/data/order';
import { useRepeatFailureOrderRescheduleFormData } from 'src/app/hooks/feature_data/useRepeatFailureOrderRescheduleFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { rescheduleDeliveryWindows } from 'src/app/router/paths';

import { BagIcon, ClockIcon, MapPinIcon } from './icons';
import { FailedOrderRescheduleLayout } from './Layout/FailedOrderRescheduleLayout';
import type { RescheduleOrderPathParams } from './types';

const UnorderedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s3};

  & > li {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.space.s2};
  }

  & > li > svg {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

const TrackEventViewDoormanConfirmation = ({ delivery_address_id }: { delivery_address_id: string }) => {
  useSegmentEvent('viewDoormanConfirmation', { shouldTrackOnMount: true, payload: { delivery_address_id } });
  return null;
};

export const DoormanConfirmationStep = () => {
  const { push } = useHistory();
  const { orderId } = useParams<RescheduleOrderPathParams>();

  const trackSelectNotDoormanAddress = useSegmentEvent('selectNotDoormanAddress', { shouldTrackOnMount: false });

  const { persistedFormData, setPersistedFormData } = useRepeatFailureOrderRescheduleFormData();

  const { data: order, isLoading: isLoadingOrder } = useGetOrder(orderId);
  const { mutateAsync: asyncUpdateDeliveryAddress, isLoading: isLoadingDeliveryAddress } = useDeliveryAddressUpdate();

  const isLoading = isLoadingOrder || isLoadingDeliveryAddress;
  const delivery_address_id = order?.deliveryAddress?.id ?? '';

  return (
    <FailedOrderRescheduleLayout
      title="Please confirm your address has a doorman"
      description={
        <>
          {!!delivery_address_id && <TrackEventViewDoormanConfirmation delivery_address_id={delivery_address_id} />}

          <p
            css={css`
              margin-bottom: ${({ theme }) => theme.space.s3};
            `}
          >
            In order for Capsule to make this delivery, your doorman must be:
          </p>

          <UnorderedList>
            <li>
              <MapPinIcon /> Physically present
            </li>
            <li>
              <ClockIcon /> Available at time of delivery
            </li>
            <li>
              <BagIcon /> Able to accept packages
            </li>
          </UnorderedList>
        </>
      }
      cta1={{
        children: 'Confirm doorman Address',
        isSubmitting: isLoading,
        onClick: async () => {
          await asyncUpdateDeliveryAddress({
            ...editDeliveryAddressRequestBodyTransform(order!.deliveryAddress),
            instructions: persistedFormData?.instructions ?? order!.deliveryAddress?.instructions ?? '',
            isDoorman: persistedFormData?.is_doorman ?? order!.deliveryAddress?.isDoorman ?? false,
          });

          push(rescheduleDeliveryWindows.toPath({ orderId }));
        },
      }}
      cta2={
        <Dialog
          title="Thanks, we've removed the “leave with doorman” instruction."
          triggerButton={
            <Button
              variant="outline-lg"
              onClick={async () => {
                setPersistedFormData({ instructions: persistedFormData?.instructions ?? '', is_doorman: false });
                trackSelectNotDoormanAddress({ delivery_address_id });

                await asyncUpdateDeliveryAddress({
                  ...editDeliveryAddressRequestBodyTransform(order!.deliveryAddress),
                  instructions: persistedFormData?.instructions ?? order!.deliveryAddress?.instructions ?? '',
                  isDoorman: false,
                });
              }}
              css={css`
                margin-top: ${({ theme }) => theme.space.s3};
              `}
            >
              Not doorman address
            </Button>
          }
        >
          <RadixDialogPrimitives.Close asChild>
            <Button
              variant="contained-secondary-lg"
              isFullWidth
              isLoading={isLoading}
              onClick={() => push(rescheduleDeliveryWindows.toPath({ orderId }))}
            >
              Ok
            </Button>
          </RadixDialogPrimitives.Close>
        </Dialog>
      }
    >
      <DoormanArt />
    </FailedOrderRescheduleLayout>
  );
};
